import { useStore } from '@/store';
import router from '@/routes';
import { nextTick, unref } from 'vue';
import { cloneDeep, get } from 'lodash';
import type { RouteLocationNormalizedLoaded, RouteLocationRaw } from 'vue-router';
import { METRICS_CUSTOM_COLUMN, METRICS_CUSTOM_EVENT, METRICS_CUSTOM_EVENT_REVENUE, METRICS_CUSTOM_EVENT_HIDDEN_REVENUE } from '@/services/constants/report';
import { getReportTypeFromRouteName } from '@/views/reports/report-route-behavior';

export type Columns = {[type: string]: number }

export interface ColumnOrder {
  value: string;
  text: string;
}

export interface TypeConfig {
  width: {[type: string]: number };
  order: ColumnOrder[];
  sort: ColumnsSort;
}

export interface UserConfig {
  bookmarks: Bemob.Bookmark[];
  desktopViewForced: boolean
  types?: {[type: string]: TypeConfig }, // obsolete only for import
  queryColumns?: {[type: string]: any }, // obsolete only for import
}

export interface LocalStorageUserData {
  users?: {[type: string]: UserConfig }
}

export const ColumnsLocalStorageKey = 'table-config';

export class ColumnsService {
  columnsConfig: Bemob.Metrics = null;

  getColumnWeight(columnName: string): number {
    const option = get(this.columnsConfig, ['metrics_options', columnName]);

    if (option) {
      return option
    }

    if (columnName && (columnName.startsWith(METRICS_CUSTOM_EVENT) ||
      columnName.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE) || columnName.startsWith(METRICS_CUSTOM_EVENT_REVENUE) || columnName.startsWith(METRICS_CUSTOM_COLUMN))) {
      return 100
    }

    return null
  }

  getDefaultLocalStorageData(): LocalStorageUserData {
    return {
      users: {}
    };
  }

  getDefaultUserData(): UserConfig {
    return {
      bookmarks: [],
      desktopViewForced: useStore().mobile.isMobile
        ? false
        : null
    };
  }

  get type() {
    return this.getType(router.currentRoute);
  }

  getType(currentRoute: RouteLocationNormalizedLoaded | RouteLocationRaw | { value: RouteLocationNormalizedLoaded }) {
    const unwrapped = unref(currentRoute as RouteLocationNormalizedLoaded);
    const route = (
      unwrapped
      && typeof unwrapped === 'object'
      && 'params' in unwrapped
    )
      ? unwrapped
      : unref((currentRoute as { value: RouteLocationNormalizedLoaded }).value);
    const routeName = typeof route === 'object' && route && 'name' in route
      ? route.name
      : undefined;
    const routeType =
      typeof route === 'object' && route && 'params' in route && route.params
        ? (route.params as { type?: string | string[] }).type
        : undefined;

    return getReportTypeFromRouteName(
      routeName,
      Array.isArray(routeType) ? routeType[0] : routeType,
    ) ?? undefined;
  }

  get localStorageUserData(): LocalStorageUserData {
    return window.localStorage[ColumnsLocalStorageKey] 
      ? JSON.parse(window.localStorage[ColumnsLocalStorageKey])
      : this.getDefaultLocalStorageData();
  }

  set localStorageUserData(value: LocalStorageUserData) {
    window.localStorage.setItem(ColumnsLocalStorageKey, JSON.stringify(value));
  }

  get currentUserData(): UserConfig {
    const { id } = useStore().user;
    return this.getUserConfig(id);
  }

  fixCurrentUserData() {
    const { id } = useStore().user;
    let data = this.localStorageUserData;

    delete data.users[id].queryColumns;
    delete data.users[id].types;

    this.localStorageUserData = data;
  }

  getUserConfig(id: string) {
    let data = this.localStorageUserData;
    if(!data.users[id]) {
      data.users[id] = this.getDefaultUserData();
      if(id) {
        nextTick(() => {
          this.localStorageUserData = data;
        });
      }
    }

    return data.users[id];
  }

  setCurrentBookmarks(bookmarks: Bemob.Bookmark[]) {
    let data = this.localStorageUserData;

    const { id } = useStore().user;
  
    if(id) {
      data.users[id].bookmarks = bookmarks;
    }

    this.localStorageUserData = data;
  }

  setCurrentDesktopViewForced(desktopViewForced: boolean) {
    let data = this.localStorageUserData;
    const id = useStore().user.id;

    if(id && data.users[id]) {
      data.users[id].desktopViewForced = desktopViewForced;
    }

    this.localStorageUserData = data;
  }

  get isReport() {
    return !!router.currentRoute.value.params.report;
  }

}

export default new ColumnsService();
